/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import {
	Card,
	CircularProgress,
	Drawer,
	Icon,
	IconButton,
	TablePagination,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ConversationTab from "./ConversationsTab";
import ResourceActions from "redux-react/actions/resourceActions";

/**
 * Displays a paginated list of conversations.
 * @param {Object} values - Values used to resolve the current contact.
 * @param {Object} action - Action configuration.
 * @param {string} resourceCode - Resource code to query.
 * @param {number} open - Open tab.
 * @param {function} onCountChange - Callback fired when the total count changes.
 * @returns {JSX.Element} The rendered listing.
 */
const ConversationsListing = ({ values, action, resourceCode, open, onCountChange = () => {} }) => {
	const [conversations, setConversations] = useState([]);
	const [conversationsCount, setConversationsCount] = useState(0);
	const [visualiseConversationLoading, setVisualiseConversationLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(40);

	const dispatch = useDispatch();

	/**
	 * Fetches conversations for the current contact.
	 * @param {string} id - Contact identifier.
	 * @param {string} resourceCode - Resource code to query.
	 * @param {number} queryPage - Page number (1-based).
	 * @param {number} queryPageSize - Number of rows per page.
	 */
	const getConversation = async (id, resourceCode, queryPage, queryPageSize) => {
		const onSuccess = res => {
			setConversations(res);
			setVisualiseConversationLoading(false);
		};
		const onFailure = () => {
			setConversations([]);
			setVisualiseConversationLoading(false);
		};

		const onSuccessCount = res => {
			const nextCount = res?.count ?? 0;
			setConversationsCount(nextCount);
			onCountChange(nextCount);
		};
		const onFailureCount = () => {
			setConversationsCount(0);
			onCountChange(0);
		};

		dispatch(
			ResourceActions.getResources(
				resourceCode,
				{
					page: queryPage,
					pageSize: queryPageSize,
					filter: `contact.FRU="${id}"`,
					sort: "-meta.cockpit.ts"
				},
				onSuccess,
				onFailure
			)
		);
		dispatch(
			ResourceActions.getResourcesCount(
				resourceCode,
				{ "contact.FRU": id },
				onSuccessCount,
				onFailureCount
			)
		);
	};

	useEffect(() => {
		if (!open) {
			setConversations([]);
			setVisualiseConversationLoading(true);
			setPage(0);
			setPageSize(40);
			setConversationsCount(0);
			onCountChange(0);
		}
	}, [open]);

	useEffect(() => {
		const id = values?._id;
		const activeResourceCode = resourceCode || action?.config?.resourceCode;

		if (
			!lod_.isNil(id) &&
			!lod_.isEmpty(id) &&
			!lod_.isNil(activeResourceCode) &&
			!lod_.isEmpty(activeResourceCode)
		) {
			setVisualiseConversationLoading(true);
			getConversation(id, activeResourceCode, page + 1, pageSize);
		}
	}, [page, pageSize, resourceCode]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setPageSize(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<MDBox sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
			{visualiseConversationLoading ? (
				<MDBox
					sx={{ flex: 1, minHeight: 0 }}
					display="flex"
					alignItems="center"
					justifyContent="center"
				>
					<CircularProgress color="info" size={60} />
				</MDBox>
			) : (
				<>
					<MDBox p={2} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
						<ConversationTab
							conversations={conversations}
							resourceCode={resourceCode || action?.config?.resourceCode}
						/>
					</MDBox>
					<TablePagination
						component="div"
						count={conversationsCount || 0}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={pageSize}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[]}
						labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
						sx={{
							"& .MuiTablePagination-toolbar": {
								justifyContent: "flex-start",
								pl: 2
							},
							"& .MuiTablePagination-spacer": {
								flex: "0 0 0"
							}
						}}
					/>
				</>
			)}
		</MDBox>
	);
};

export default ConversationsListing;
